import { AlsoIn } from "./AlsoIn";
import { setTaskWorkspaces } from "../core/links";
import { AssignEditor } from "./AssignEditor";
import { formatPeriod } from "./period";
import { useMuteBadges } from "../app/useMuteBadges";
import { PlannedSlots } from "./PlannedSlots";
import { plansOf, readPlanner, type PlannedSlot } from "../planner/ops";
import { RepeatEditor } from "./RepeatEditor";
import { useLevels } from "../app/levels";
import { useSession } from "../app/session";
import { MeetEditor, type Person } from "../signals/MeetEditor";
import { readCircle } from "../circle/useCircle";
import { useMyPlaces } from "../signals/myPlaces";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { setDone } from "./earlyDone";
import { NotNowButton } from "./NotNow";
import { useTranslation } from "react-i18next";
import { HelpLink } from "../app/HelpLink";
import { useNavigate } from "react-router";
import {
  Archive,
  Bell,
  ChevronRight,
  CornerLeftUp,
  ListOrdered,
  Maximize2,
  Play,
  Plus,
  Square,
  X,
} from "lucide-react";
import { clsx } from "clsx";
import { Button, Checkbox, Chip, Input, Pill, Textarea, Why, usePopover, FoldToggle } from "../ui";
import type { ViewCtx } from "../registry/viewTypes";
import { useDocSelector } from "../core/useDocSelector";
import { childrenOf, listTasks, readTask } from "../core/tasks";
import { computeAll } from "../core/computed";
import { wantDisplay, wantLevel, DEFAULT_PERIOD_DAYS } from "../core/want";
import {
  addEdge,
  addToGroup,
  archiveTask,
  createGroup,
  createTask,
  listPropDefs,
  removeEdge,
  removeFromGroup,
  setNotes,
  setOrderedChildren,
  setProp,
  setTitle,
  snapshotOf,
  unarchiveTask,
} from "../core/ops";
import { blockers, relatesOf, sequenceHead, sequencesOf } from "../core/structure";
import type { Level, PropDef, PropVal, Task } from "../core/schema";
import { fmtElapsed, fmtMinutes, fmtRelativeAbsolute, round1 } from "../core/format";
import { readCurrent, startTracking, stopTracking, elapsedMs } from "../tracking/timelog";
import { PropEditor, ReadOnlyValue } from "./PropEditor";
import { isReadOnlyProp, propGroupOf, propLabel, propSpan, type PropGroup } from "./propLayout";
import { WhenEditor } from "../signals/WhenEditor";
import type { SignalDef } from "../signals/describe";
import { useFold } from "./useFold";
import { Attachments } from "./Attachments";
import { RemoteActions } from "./RemoteActions";
import { useCircle } from "../circle/useCircle";
import { useToasts } from "../ui/toasts";
import { addReminder, listReminders, removeReminder } from "../reminders/reminders";
import { askNotificationPermission } from "../app/notify";
import { setTaskType } from "../core/ops";
import { TaskPicker } from "./TaskPicker";
import { WantBar } from "./WantBar";

const LEVELS: Level[] = ["simple", "standard", "power"];
const heading = "text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500";

/** One relation of the task: a fixed-width label on the left, chips and the adder wrapping on the right. */
function LinkRow({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] items-start gap-x-3" data-testid={testId}>
      <span className="pt-[5px] text-[12px] text-neutral-500">{label}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-1">{children}</div>
    </div>
  );
}
const visibleAt = (def: PropDef, level: Level) =>
  LEVELS.indexOf(def.level ?? "power") <= LEVELS.indexOf(level);

/** Local draft for a text field: shows the doc value until the user types, commits after `delay`. */
function useDebouncedText(
  value: string,
  commit: (v: string) => void,
  delay = 400,
): [string, (v: string) => void, () => void] {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) return;
    const h = setTimeout(() => {
      commit(draft);
      setDirty(false);
    }, delay);
    return () => clearTimeout(h);
  }, [draft, dirty, delay, commit]);
  return [
    dirty ? draft : value,
    (v) => {
      setDraft(v);
      setDirty(true);
    },
    () => {
      if (dirty) {
        commit(draft);
        setDirty(false);
      }
    },
  ];
}

export function TaskPanel({ taskId, ctx, onClose }: { taskId: string; ctx: ViewCtx; onClose?: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // what the panel shows is its own per-person choice (ADR 0019), not the Settings level
  const panelLevel = useLevels().panel;
  useReportOpenTask(taskId, ctx.workspaceId);
  const task = useDocSelector(ctx.handle, (doc) => readTask(doc, taskId), [taskId]);
  // where the planner has this task (as text: the same plan gives the same value, and no new render)
  const placeCtxJson = useDocSelector(ctx.handle, (doc) =>
    JSON.stringify(
      Object.values(
        (
          doc.getMap("settings").toJSON() as {
            places?: Record<string, { name?: string; contexts?: string[] }>;
          }
        ).places ?? {},
      ).map((pl) => ({ name: pl?.name ?? "", contexts: Array.isArray(pl?.contexts) ? pl.contexts : [] })),
    ),
  );
  const slotsJson = useDocSelector(ctx.handle, (doc) => JSON.stringify(plansOf(readPlanner(doc), taskId)), [
    taskId,
  ]);
  const slots = useMemo(() => JSON.parse(slotsJson ?? "[]") as PlannedSlot[], [slotsJson]);
  // a dependency that cannot be added (a loop): said beside the picker, not in a browser alert
  const [depError, setDepError] = useState<string | null>(null);
  const tasks = useDocSelector(ctx.handle, (doc) => listTasks(doc)) ?? [];
  const propDefs = useDocSelector(ctx.handle, (doc) => listPropDefs(doc)) ?? [];
  // signal definitions and place names for "When" (I8), as one string so the selector stays stable
  const signalSlice = useDocSelector(ctx.handle, (doc) => {
    const st = doc.getMap("settings").toJSON() as {
      signals?: Record<string, SignalDef>;
      places?: Record<string, { name: string }>;
      placeLists?: Record<string, { name: string }>;
    };
    return JSON.stringify({
      definitions: st.signals ?? {},
      places: Object.values(st.places ?? {}).map((p) => p.name),
      placeList: Object.entries(st.places ?? {})
        .map(([id, p]) => ({ id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      placeLists: Object.entries(st.placeLists ?? {})
        .map(([id, p]) => ({ id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  });
  // circle mode (P2): the people a task can be "with", from the hidden `with` property the server keeps
  const peopleJson = useDocSelector(ctx.handle, (doc) =>
    JSON.stringify(
      ((doc.getMap("propDefs").toJSON() as Record<string, { spec?: { values?: Person[] } }>).with?.spec
        ?.values ?? null) as Person[] | null,
    ),
  );
  const people = JSON.parse(peopleJson ?? "null") as Person[] | null;
  const meId = useSession((s) => s.session?.profile?.id ?? null);
  // in a shared space, my private places count too (P1)
  const sharedSpace = useDocSelector(ctx.handle, (doc) => readCircle(doc).shared) ?? false;
  const myPlaces = useMyPlaces(sharedSpace);
  const groups =
    useDocSelector(
      ctx.handle,
      (doc) => doc.getMap("groups").toJSON() as Record<string, { title: string; color: string | null }>,
    ) ?? {};
  const sequences =
    useDocSelector(
      ctx.handle,
      (doc) => doc.getMap("sequences").toJSON() as Record<string, { title: string }>,
    ) ?? {};
  const edges =
    useDocSelector(
      ctx.handle,
      (doc) => doc.getMap("edges").toJSON() as Record<string, { kind: string; from: string; to: string }>,
    ) ?? {};
  const current = useDocSelector(ctx.handle, (doc) => readCurrent(doc));
  const children = useDocSelector(ctx.handle, (doc) => (task ? childrenOf(doc, task) : []), [taskId]);
  const snapshot = useMemo(() => snapshotOf(ctx.doc), [ctx.doc, tasks, edges, groups, sequences]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isOpen, toggleFold] = useFold();
  const pushToast = useToasts((s) => s.push);
  const circle = useCircle(ctx.handle);
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const [remindAt, setRemindAt] = useState("");
  const reminders =
    useDocSelector(ctx.handle, (doc) => listReminders(doc).filter((r) => r.taskId === taskId), [taskId]) ??
    [];

  const commitTitle = useMemo(
    () => (v: string) => ctx.write((doc) => setTitle(doc, taskId, v)),
    [ctx, taskId],
  );
  const commitNotes = useMemo(
    () => (v: string) => ctx.write((doc) => setNotes(doc, taskId, v)),
    [ctx, taskId],
  );
  const [title, setTitleDraft, flushTitle] = useDebouncedText(task?.title ?? "", commitTitle);
  const [notes, setNotesDraft, flushNotes] = useDebouncedText(task?.notes ?? "", commitNotes);

  if (!task) {
    return (
      <aside className="w-full max-w-md overflow-y-auto border-l border-neutral-200 bg-[var(--surface-2)] p-4 dark:border-neutral-800">
        <p className="text-neutral-500">{t("task.notFound")}</p>
      </aside>
    );
  }

  const byId = new Map(tasks.map((x) => [x.id, x]));
  const parentId = snapshot.parentOf(taskId);
  const computed = computeAll(snapshot, taskId, ctx.now);
  const blockedBy = blockers(snapshot, taskId);
  const related = relatesOf(snapshot, taskId);
  const seqIds = sequencesOf(snapshot, taskId);
  const dependsOut = Object.entries(edges).filter(([, e]) => e.kind === "depends" && e.from === taskId);
  const dependsIn = Object.entries(edges).filter(([, e]) => e.kind === "depends" && e.to === taskId);
  const relatesEdges = Object.entries(edges).filter(
    ([, e]) => e.kind === "relates" && (e.from === taskId || e.to === taskId),
  );
  const myGroups = Object.entries(groups).filter(([gid]) => snapshot.groups[gid]?.members?.[taskId]);
  const isRecurring = task.typeId === "recurring";
  const isNote = task.typeId === "note";
  const repeats = isRecurring || typeof task.props["repeat.rrule"] === "string";
  const tracking = current?.taskId === taskId;
  const done = task.doneAt !== null;
  const ro = ctx.readOnly;
  const atLeastStandard = panelLevel !== "simple";

  const defs = propDefs.filter((d) => {
    if (d.id.startsWith("rec.")) return isRecurring;
    if (isNote) return d.kind === "tag" || d.kind === "enum";
    return true;
  });
  // mirrored values are listed, not edited, and only when they have a value
  const mirrored = defs.filter(
    (d) => isReadOnlyProp(d) && task.props[d.id] !== undefined && task.props[d.id] !== "",
  );
  // `repeat.rrule` and the period are set through the Repeats control (in Timing): the rule is never typed by
  // hand, and the period is not shown twice
  const editable = defs.filter(
    (d) =>
      !isReadOnlyProp(d) &&
      visibleAt(d, panelLevel) &&
      d.id !== "repeat.rrule" &&
      d.id !== "rec.periodDays" &&
      d.id !== "assignee",
  );
  // how many values are set: each value of a list (contexts, people) counts; empty and cleared ones do not
  const setCount = (ds: PropDef[]) =>
    ds.reduce((n, d) => {
      const v = task.props[d.id];
      if (Array.isArray(v)) return n + v.length;
      return n + (v === undefined || v === null || v === "" ? 0 : 1);
    }, 0);
  const editProp = (d: PropDef) => (v: PropVal | null) => ctx.write((doc) => setProp(doc, taskId, d.id, v));
  // One group per question, each folded or open by the person's own click only (remembered per device). An open
  // group shows all its fields, filled or not, so a value set or cleared never moves anything. A group with no
  // field and nothing else to show is left out.
  const group = (id: PropGroup, extra: number, children: ReactNode) => {
    const ds = editable.filter((d) => propGroupOf(d) === id);
    if (ds.length === 0 && !children) return null;
    return (
      <FoldSection
        key={id}
        id={id}
        label={t(`task.group.${id}`)}
        count={setCount(ds) + extra}
        open={isOpen(id)}
        onToggle={() => toggleFold(id)}
      >
        {ds.length > 0 && (
          <PropBlock defs={ds} task={task} ro={ro} edit={editProp} tagsLabel={t("task.tags")} />
        )}
        {children}
      </FoldSection>
    );
  };
  // places that remind of this task through one of its contexts (Settings → Signals → Places → Reminders): said in
  // *Remind me*, where it was not shown before
  const taskContexts = Array.isArray(task.props.context) ? (task.props.context as string[]) : [];
  const placeContexts = (JSON.parse(placeCtxJson ?? "[]") as { name: string; contexts: string[] }[])
    .filter((pl) => pl.contexts.some((c) => taskContexts.includes(c)))
    .map((pl) => pl.name);

  const close = () => (onClose ? onClose() : navigate("/"));
  const signalSettings = JSON.parse(signalSlice ?? "{}") as {
    definitions: Record<string, SignalDef>;
    places: string[];
    placeList: { id: string; name: string }[];
    placeLists: { id: string; name: string }[];
  };

  return (
    <aside
      className="flex w-full max-w-md flex-col gap-4 overflow-y-auto *:shrink-0 border-l border-neutral-200 bg-[var(--surface-2)] p-4 dark:border-neutral-800"
      data-testid="task-panel"
      data-task-id={taskId}
      aria-label={t("task.detail")}
    >
      {parentId && (
        <button
          type="button"
          onClick={() => ctx.openTask(parentId)}
          className="-mb-2 inline-flex min-w-0 items-center gap-1 self-start text-[12px] text-neutral-500 hover:text-accent"
          data-testid="panel-parent"
        >
          <CornerLeftUp size={12} aria-hidden />
          <span className="truncate">{t("task.partOf", { title: byId.get(parentId)?.title ?? "…" })}</span>
        </button>
      )}
      <header className="flex items-start gap-2">
        {!isNote && (
          <Checkbox
            className="mt-[7px]"
            checked={done}
            disabled={ro}
            label={done ? t("task.undone") : t("task.done")}
            onChange={(v) => {
              setDone(ctx.write, t, i18n.language, task, v);
            }}
          />
        )}
        <Input
          aria-label={t("task.title")}
          className="px-2.5 py-1.5 text-[15px] font-semibold"
          value={title}
          disabled={ro}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={flushTitle}
          data-testid="task-title"
        />
        <HelpLink page="guides/tasks/" className="h-[30px] w-[30px]" />
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("common.close")}
          onClick={close}
          className="w-[30px] shrink-0 px-0 touch:h-11 touch:w-11"
        >
          <X size={16} aria-hidden />
        </Button>
      </header>

      {/* state row: pills with words, never colour alone; the timer sits on the same line */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Pill tone="neutral">{t(`types.${task.typeId}`, { defaultValue: task.typeId })}</Pill>
        {blockedBy.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <Pill tone="warn">
              {t("task.blockedBy", { title: byId.get(blockedBy[0])?.title ?? "?", n: blockedBy.length - 1 })}
            </Pill>
            <Why
              title={t("why.blocked")}
              formula={t("why.blockedFormula")}
              rows={blockedBy.map((b) => [byId.get(b)?.title ?? b, t("task.open")])}
            />
          </span>
        )}
        {seqIds.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <Pill tone={computed.active ? "ok" : "neutral"}>
              {computed.active ? t("task.active") : t("task.queued")}
            </Pill>
            <Why
              title={t("why.active")}
              formula={t("why.activeFormula")}
              rows={seqIds.map((sid) => [
                sequences[sid]?.title ?? sid,
                byId.get(sequenceHead(snapshot, sid) ?? "")?.title ?? "—",
              ])}
            />
          </span>
        )}
        {computed.overdueDays !== null && (computed.overdueDays as number) > 0 && (
          <span className="inline-flex items-center gap-1">
            <Pill tone="warn">{t("task.later")}</Pill>
            <Why
              title={t("why.overdue")}
              rows={[
                [t("props.due"), fmtRelativeAbsolute(task.props.due as string, ctx.now, i18n.language, t)],
              ]}
            />
          </span>
        )}
        {/* who finished it, in a shared space (UI cleanup U3: `doneBy` was written but never shown) */}
        {circle.shared && task.doneAt && task.doneBy && (
          <span className="text-[12px] text-neutral-500" data-testid="task-done-by">
            {t("circle.doneBy", {
              name: circle.nameOf(String(task.doneBy).replace(/^profile:/, "")),
              when: fmtRelativeAbsolute(task.doneAt, ctx.now, i18n.language, t),
            })}
          </span>
        )}
        {!isNote && ctx.profileKind !== "kid" && (
          <span className="ml-auto flex items-center gap-1.5">
            {(computed.timeSpentMin as number) > 0 && !tracking && (
              <span className="text-[12px] text-neutral-500">
                {t("tracking.total", { d: fmtMinutes(computed.timeSpentMin as number) })}
              </span>
            )}
            {tracking && current && (
              <span className="font-mono text-[13px] tabular-nums" data-testid="elapsed">
                {fmtElapsed(elapsedMs(current, ctx.now))}
              </span>
            )}
            <Button
              variant={tracking ? "secondary" : "primary"}
              size="sm"
              disabled={ro}
              data-testid="track-toggle"
              onClick={() =>
                ctx.write(
                  () =>
                    void (tracking ? stopTracking(ctx.doc) : startTracking(ctx.doc, ctx.workspaceId, taskId)),
                )
              }
            >
              {tracking ? <Square size={14} aria-hidden /> : <Play size={14} aria-hidden />}
              {tracking ? t("tracking.stop") : t("tracking.start")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/focus/${taskId}`)}
              data-testid="focus-open"
              aria-label={t("task.focus")}
              title={t("task.focus")}
              className="w-[30px] px-0"
            >
              <Maximize2 size={14} aria-hidden />
            </Button>
          </span>
        )}
        {!ro && !isNote && !task.doneAt && (
          <NotNowButton
            task={task}
            shared={sharedSpace}
            write={(fn) => ctx.write(fn)}
            openTask={ctx.openTask}
          />
        )}
      </div>

      {isRecurring && (
        <section className="flex flex-col gap-1.5" data-testid="want-section">
          <div className="flex items-center justify-between text-[12.5px] text-neutral-500">
            <span className="inline-flex items-center gap-1">
              {t("want.label")}
              <Why
                title={t("want.label")}
                formula="want = (age / period) ^ elasticity"
                rows={[
                  [t("want.age"), `${round1(computed.daysSinceDone as number)} d`],
                  [
                    t("props.rec.periodDays"),
                    formatPeriod(
                      t,
                      Number(task.props["rec.periodDays"]) || DEFAULT_PERIOD_DAYS,
                      task.props["rec.periodUnit"],
                    ),
                  ],
                  [t("props.rec.elasticity"), String(task.props["rec.elasticity"] ?? 1)],
                  [t("props.rec.minGapDays"), `${task.props["rec.minGapDays"] ?? 0} d`],
                  [t("want.label"), String(round1(computed.want as number))],
                ]}
              />
            </span>
            <span className="font-mono">{round1(computed.want as number)}</span>
          </div>
          <WantBar
            value={wantDisplay(computed.want as number, task.props["rec.ceiling"] as number | undefined)}
            level={wantLevel(computed.want as number)}
            ceiling={(task.props["rec.ceiling"] as number) || 3}
          />
          <p className="text-[12px] text-neutral-500">
            {computed.daysSinceDone === null
              ? t("want.neverDone")
              : t("want.daysSinceDone", { n: round1(computed.daysSinceDone as number) })}
          </p>
        </section>
      )}

      {/* the panel answers six questions, one group each (UI cleanup U3): what is it, when, remind me, how big, where
          and with whom, how it connects; then the person's own fields and values mirrored from elsewhere */}
      <div className="flex flex-col" data-testid="props">
        {group(
          "what",
          0,
          <>
            {isNote && !ro && (
              <Button
                variant="secondary"
                size="sm"
                className="self-start"
                data-testid="make-task"
                onClick={() => ctx.write((doc) => setTaskType(doc, taskId, "task"))}
              >
                {t("task.makeTask")}
              </Button>
            )}
            <section>
              <h3 className={clsx(heading, "mb-1")}>{t("task.notes")}</h3>
              <Textarea
                rows={isNote ? 12 : 4}
                value={notes}
                disabled={ro}
                aria-label={t("task.notes")}
                onChange={(e) => setNotesDraft(e.target.value)}
                onBlur={flushNotes}
                data-testid="task-notes"
              />
            </section>
          </>,
        )}
        {!isNote &&
          group(
            "timing",
            (repeats ? 1 : 0) + slots.length,
            <>
              <PlannedSlots slots={slots} ro={ro} write={(fn) => ctx.write(fn)} />
              {!isNote && (
                <RepeatEditor
                  // a new key when the values change elsewhere (quick-add, another device): the fields start fresh
                  key={`${task.id}|${String(task.props["rec.periodDays"] ?? "")}|${String(task.props["rec.periodUnit"] ?? "")}|${String(task.props["repeat.rrule"] ?? "")}`}
                  task={task}
                  ro={ro}
                  write={(fn) => ctx.write(fn)}
                  allowFixed={panelLevel !== "simple"}
                />
              )}
            </>,
          )}
        {!isNote &&
          group(
            "remind",
            reminders.length +
              (task.props.when ? 1 : 0) +
              (task.props.atPlace ? 1 : 0) +
              (task.props.whenWeMeet ? 1 : 0),
            <>
              {!isNote && (
                <LinkRow label={t("task.remindAtTime")} testId="reminders">
                  {reminders.map((r) => (
                    <Chip
                      key={r.id}
                      removeLabel={t("reminders.remove")}
                      onRemove={ro ? undefined : () => ctx.write((doc) => removeReminder(doc, r.id))}
                    >
                      <Bell size={12} aria-hidden /> {fmtRelativeAbsolute(r.at, ctx.now, i18n.language, t)}
                    </Chip>
                  ))}
                  {!ro && (
                    <form
                      className="flex min-w-0 flex-1 items-center gap-1.5"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!remindAt) return;
                        void askNotificationPermission(); // asked only here, when the first reminder is made (§9.24)
                        ctx.write((doc) =>
                          addReminder(
                            doc,
                            taskId,
                            new Date(remindAt),
                            undefined,
                            circle.shared ? circle.me : null,
                          ),
                        );
                        setRemindAt("");
                      }}
                    >
                      <Input
                        compact
                        type="datetime-local"
                        className="min-w-0 flex-1"
                        value={remindAt}
                        onChange={(e) => setRemindAt(e.target.value)}
                        aria-label={t("reminders.add")}
                        data-testid="reminder-input"
                      />
                      <Button
                        type="submit"
                        variant="secondary"
                        size="sm"
                        disabled={!remindAt}
                        aria-label={t("reminders.add")}
                        title={t("reminders.add")}
                        className="w-[30px] shrink-0 px-0"
                      >
                        <Plus size={14} aria-hidden />
                      </Button>
                    </form>
                  )}
                </LinkRow>
              )}
              <WhenEditor
                task={task}
                ro={ro}
                write={(fn) => ctx.write(fn)}
                definitions={signalSettings.definitions}
                places={signalSettings.places}
                placeList={signalSettings.placeList}
                placeLists={signalSettings.placeLists}
                myPlaces={myPlaces.places}
                myLists={myPlaces.lists}
                workspaceId={ctx.workspaceId}
              />
              {placeContexts.length > 0 && (
                <p className="text-[12px] text-neutral-500" data-testid="remind-by-context">
                  {t("task.remindByContext", { places: placeContexts.join(", ") })}
                </p>
              )}
              {people && (
                <MeetEditor task={task} ro={ro} write={(fn) => ctx.write(fn)} people={people} me={meId} />
              )}
            </>,
          )}
        {group("effort", 0, null)}
        {!isNote &&
          group(
            "place",
            0,
            <>
              {circle.shared && !isNote && (
                <AssignEditor
                  task={task}
                  members={circle.members}
                  me={circle.me}
                  nameOf={circle.nameOf}
                  repeats={repeats}
                  ro={ro}
                  write={(fn) => ctx.write(fn)}
                />
              )}
            </>,
          )}
        {group(
          "links",
          myGroups.length + dependsOut.length + relatesEdges.length + (children?.length ?? 0),
          <>
            {!isNote && (
              <section data-testid="subtasks">
                <div className="mb-1 flex items-center gap-2">
                  <h3 className={heading}>
                    {t("task.subtasks")}{" "}
                    {children && children.length > 0 && (
                      <span className="font-mono">
                        {computed.childrenDone as number}/{computed.childrenTotal as number}
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    aria-pressed={task.orderedChildren}
                    title={t("task.orderedChildren")}
                    aria-label={t("task.orderedChildren")}
                    disabled={ro}
                    className={clsx(
                      "rounded-sm p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800",
                      "ml-auto",
                      task.orderedChildren &&
                        "bg-neutral-200 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-100",
                    )}
                    onClick={() => ctx.write((doc) => setOrderedChildren(doc, taskId, !task.orderedChildren))}
                  >
                    <ListOrdered size={14} aria-hidden />
                  </button>
                </div>
                {children && children.length > 0 && (
                  <ul className="kss-surface mb-1.5 divide-y divide-neutral-200 dark:divide-neutral-800">
                    {children.map((cid, i) => {
                      const c = byId.get(cid);
                      if (!c || c.archivedAt) return null;
                      const nextUndone =
                        task.orderedChildren &&
                        children.slice(0, i).every((p) => byId.get(p)?.doneAt) &&
                        !c.doneAt;
                      return (
                        <li
                          key={cid}
                          className={clsx(
                            "flex items-center gap-2 px-2.5 py-1.5",
                            nextUndone && "bg-accent-tint/60",
                          )}
                          data-testid="subtask-row"
                        >
                          {task.orderedChildren && (
                            <span className="w-4 font-mono text-[11px] text-neutral-400">{i + 1}</span>
                          )}
                          <Checkbox
                            checked={c.doneAt !== null}
                            disabled={ro}
                            label={c.doneAt ? t("task.undone") : t("task.done")}
                            onChange={(v) => {
                              setDone(ctx.write, t, i18n.language, c, v);
                            }}
                          />
                          {/* the title is edited in place; opening the subtask is the arrow, never a click on the text */}
                          <input
                            key={c.title}
                            defaultValue={c.title}
                            disabled={ro}
                            aria-label={t("task.subtaskTitle")}
                            data-testid="subtask-title"
                            className={clsx(
                              "min-w-0 flex-1 truncate rounded-sm border border-transparent bg-transparent px-1 py-0.5 text-[13.5px] hover:border-neutral-200 focus:border-accent focus:bg-[var(--surface)] focus:outline-none dark:hover:border-neutral-700",
                              c.doneAt && "text-neutral-500 line-through",
                            )}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.currentTarget.blur();
                              if (e.key === "Escape") {
                                e.currentTarget.value = c.title;
                                e.currentTarget.blur();
                              }
                            }}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v && v !== c.title) ctx.write((doc) => setTitle(doc, cid, v));
                              else e.target.value = c.title;
                            }}
                          />
                          <button
                            type="button"
                            aria-label={t("task.openSubtask", { title: c.title })}
                            title={t("task.openSubtask", { title: c.title })}
                            onClick={() => ctx.openTask(cid)}
                            data-testid="subtask-open"
                            className="grid h-6 w-6 shrink-0 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                          >
                            <ChevronRight size={14} aria-hidden />
                          </button>
                          {!ro && (
                            <button
                              type="button"
                              aria-label={t("task.removeSubtask", { title: c.title })}
                              title={t("task.removeSubtask", { title: c.title })}
                              data-testid="subtask-remove"
                              className="grid h-6 w-6 shrink-0 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                              onClick={() => {
                                ctx.write((doc) => archiveTask(doc, cid));
                                pushToast(t("task.archivedToast", { title: c.title }), {
                                  action: {
                                    label: t("common.undo"),
                                    run: () => ctx.write((doc) => unarchiveTask(doc, cid)),
                                  },
                                });
                              }}
                            >
                              <Archive size={13} aria-hidden />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <form
                  className="flex items-center gap-1.5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const v = subtaskTitle.trim();
                    if (!v) return;
                    ctx.write((doc) => createTask(doc, { title: v, parentId: taskId }));
                    setSubtaskTitle("");
                  }}
                >
                  <Input
                    compact
                    value={subtaskTitle}
                    disabled={ro}
                    placeholder={t("task.addSubtask")}
                    aria-label={t("task.addSubtask")}
                    title={t("task.addSubtaskHint")}
                    onChange={(e) => setSubtaskTitle(e.target.value)}
                    onPaste={(e) => {
                      // several lines pasted at once: one subtask per line (this replaced "Split into steps")
                      const lines = e.clipboardData
                        .getData("text")
                        .split(/\r?\n/)
                        .map((l) => l.trim())
                        .filter(Boolean);
                      if (lines.length < 2) return;
                      e.preventDefault();
                      ctx.write((doc) => {
                        for (const line of lines) createTask(doc, { title: line, parentId: taskId });
                      });
                    }}
                    data-testid="subtask-input"
                  />
                  <Button
                    type="submit"
                    variant="secondary"
                    size="sm"
                    disabled={ro || !subtaskTitle.trim()}
                    aria-label={t("common.add")}
                    className="w-[30px] shrink-0 px-0"
                  >
                    <Plus size={14} aria-hidden />
                  </Button>
                </form>
              </section>
            )}
            <div className="flex flex-col gap-2">
              <LinkRow label={t("task.groups")} testId="groups">
                {myGroups.map(([gid, g]) => (
                  <Chip
                    key={gid}
                    tone={g.color}
                    removeLabel={t("common.remove")}
                    onRemove={ro ? undefined : () => ctx.write((doc) => removeFromGroup(doc, gid, taskId))}
                  >
                    {g.title}
                  </Chip>
                ))}
                <GroupAdder
                  groups={groups}
                  exclude={new Set(myGroups.map(([gid]) => gid))}
                  disabled={ro}
                  onPick={(gid) => ctx.write((doc) => addToGroup(doc, gid, taskId))}
                  onCreate={(title) => ctx.write((doc) => addToGroup(doc, createGroup(doc, title), taskId))}
                />
              </LinkRow>

              {!isNote && (
                <LinkRow label={t("task.dependsOn")} testId="dependencies">
                  {dependsOut.map(([eid, e]) => (
                    <Chip
                      key={eid}
                      tone={byId.get(e.to)?.doneAt ? "ok" : "warn"}
                      removeLabel={t("common.remove")}
                      onRemove={ro ? undefined : () => ctx.write((doc) => removeEdge(doc, eid))}
                    >
                      {byId.get(e.to)?.title ?? e.to}
                    </Chip>
                  ))}
                  {!ro && (
                    <div className="min-w-[9rem] flex-1">
                      <TaskPicker
                        compact
                        tasks={tasks}
                        exclude={new Set([taskId, ...dependsOut.map(([, e]) => e.to)])}
                        placeholder={t("task.addDependency")}
                        testId="dependency-picker"
                        onPick={(id) =>
                          ctx.write((doc) => {
                            try {
                              addEdge(doc, "depends", taskId, id);
                              setDepError(null);
                            } catch (err) {
                              setDepError(
                                t(`errors.${(err as Error).message}`, { defaultValue: t("error.generic") }),
                              );
                            }
                          })
                        }
                      />
                      {depError && (
                        <p role="alert" className="mt-1 text-[12px] text-warn" data-testid="dependency-error">
                          {depError}
                        </p>
                      )}
                    </div>
                  )}
                </LinkRow>
              )}

              {!isNote && dependsIn.length > 0 && (
                <LinkRow label={t("task.blocks")} testId="blocks">
                  <span className="text-[12.5px] text-neutral-600 dark:text-neutral-300">
                    {dependsIn.map(([, e]) => byId.get(e.from)?.title ?? e.from).join(", ")}
                  </span>
                </LinkRow>
              )}

              {atLeastStandard && (
                <AlsoIn
                  here={ctx.workspaceId}
                  value={task.props["link.workspaces"]}
                  readOnly={ro}
                  onChange={(others) =>
                    ctx.write((doc) => setTaskWorkspaces(doc, taskId, ctx.workspaceId, others))
                  }
                />
              )}

              <LinkRow label={t("task.relatedTo")} testId="relations">
                {relatesEdges.map(([eid, e]) => {
                  const other = e.from === taskId ? e.to : e.from;
                  return (
                    <Chip
                      key={eid}
                      removeLabel={t("common.remove")}
                      onRemove={ro ? undefined : () => ctx.write((doc) => removeEdge(doc, eid))}
                    >
                      <button type="button" className="hover:underline" onClick={() => ctx.openTask(other)}>
                        {byId.get(other)?.title ?? other}
                      </button>
                    </Chip>
                  );
                })}
                {!ro && (
                  <div className="min-w-[9rem] flex-1">
                    <TaskPicker
                      compact
                      tasks={tasks}
                      exclude={new Set([taskId, ...related])}
                      placeholder={t("task.addRelation")}
                      onPick={(id) => ctx.write((doc) => addEdge(doc, "relates", taskId, id))}
                    />
                  </div>
                )}
              </LinkRow>

              {seqIds.length > 0 && (
                <LinkRow label={t("task.sequences")} testId="sequences">
                  {seqIds.map((sid) => (
                    <Chip key={sid} tone={sequenceHead(snapshot, sid) === taskId ? "accent" : "neutral"}>
                      {sequences[sid]?.title ?? sid}
                    </Chip>
                  ))}
                </LinkRow>
              )}
            </div>
          </>,
        )}
        {group("other", 0, null)}
        {mirrored.length > 0 && (
          <FoldSection
            id="mirrored"
            label={t("task.mirrored")}
            count={mirrored.length}
            open={isOpen("mirrored")}
            onToggle={() => toggleFold("mirrored")}
          >
            <dl
              className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12.5px]"
              aria-label={t("task.mirrored")}
              data-testid="mirrored-props"
            >
              {mirrored.map((d) => (
                <div key={d.id} className="contents">
                  <dt className="text-neutral-500">{propLabel(d, t)}</dt>
                  <dd className="flex min-w-0">
                    <ReadOnlyValue def={d} value={task.props[d.id]!} />
                  </dd>
                </div>
              ))}
            </dl>
          </FoldSection>
        )}
      </div>

      <Attachments task={task} workspaceId={ctx.workspaceId} readOnly={ro} />
      {!ro && panelLevel === "power" && <RemoteActions task={task} workspaceId={ctx.workspaceId} />}

      <footer className="mt-auto flex items-center justify-between border-t border-neutral-200 pt-2.5 text-[12px] text-neutral-500 dark:border-neutral-800">
        <span>
          {t("task.created", { when: fmtRelativeAbsolute(task.createdAt, ctx.now, i18n.language, t) })}
        </span>
        {task.archivedAt ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={ro}
            onClick={() => ctx.write((doc) => unarchiveTask(doc, taskId))}
          >
            {t("task.unarchive")}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={ro}
            data-testid="archive"
            onClick={() => {
              ctx.write((doc) => archiveTask(doc, taskId));
              pushToast(t("task.archivedToast", { title: task.title }), {
                action: {
                  label: t("common.undo"),
                  run: () => ctx.write((doc) => unarchiveTask(doc, taskId)),
                },
              });
              close();
            }}
          >
            <Archive size={14} aria-hidden />
            {t("task.archive")}
          </Button>
        )}
      </footer>
    </aside>
  );
}

/**
 * A thematic group of the panel: a hairline above, a header with a fold triangle and how many of its
 * values are set, and the content while open.
 */
function FoldSection({
  id,
  label,
  count,
  open,
  onToggle,
  children,
  plain,
}: {
  id: string;
  label: string;
  count: number;
  open: boolean;
  onToggle(): void;
  children: ReactNode;
  /** show the count as a plain number ("3") instead of "3 set" */
  plain?: boolean;
}) {
  const { t } = useTranslation();
  const mute = useMuteBadges();
  return (
    <section
      className="border-t border-neutral-200 py-2 dark:border-neutral-800"
      data-testid={`group-${id}`}
      data-open={open}
    >
      <FoldToggle
        open={open}
        onToggle={onToggle}
        heading
        testId="group-toggle"
        trailing={count > 0 && !mute ? (plain ? count : t("task.setCount", { n: count })) : null}
      >
        {label}
      </FoldToggle>
      {open && <div className="mt-2 flex flex-col gap-2.5">{children}</div>}
    </section>
  );
}

/** Tags as one chip row, then the other fields two per row, in the definitions' order. */
function PropBlock({
  defs,
  task,
  ro,
  edit,
  tagsLabel,
}: {
  defs: PropDef[];
  task: Task;
  ro: boolean;
  edit(d: PropDef): (v: PropVal | null) => void;
  tagsLabel: string;
}) {
  const tags = defs.filter((d) => d.kind === "tag");
  // half-width fields first so they pair up; the order is fixed, whatever the values
  const fields = defs
    .filter((d) => d.kind !== "tag")
    .sort((a, b) => Number(propSpan(a) === "full") - Number(propSpan(b) === "full"));
  return (
    <>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1" role="group" aria-label={tagsLabel}>
          {tags.map((d) => (
            <PropEditor key={d.id} def={d} value={task.props[d.id]} disabled={ro} onChange={edit(d)} />
          ))}
        </div>
      )}
      {fields.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {fields.map((d) => (
            <div key={d.id} className={clsx("min-w-0", propSpan(d) === "full" && "col-span-2")}>
              <PropEditor
                def={d}
                value={task.props[d.id]}
                disabled={ro}
                onChange={edit(d)}
                // the repeat settings shape the want bar: show what counts, the default when nothing is set
                fillDefault={d.id.startsWith("rec.")}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function GroupAdder({
  groups,
  exclude,
  disabled,
  onPick,
  onCreate,
}: {
  groups: Record<string, { title: string; color: string | null }>;
  exclude: Set<string>;
  disabled?: boolean;
  onPick(gid: string): void;
  onCreate(title: string): void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const { open, setOpen, ref } = usePopover<HTMLDivElement>();
  const n = q.trim().toLowerCase();
  const hits = Object.entries(groups).filter(
    ([gid, g]) => !exclude.has(gid) && (!n || g.title.toLowerCase().includes(n)),
  );
  const exact = hits.some(([, g]) => g.title.toLowerCase() === n);
  if (disabled) return null;
  return (
    <div ref={ref} className="relative">
      <Input
        compact
        className="w-36"
        placeholder={t("task.addGroup")}
        aria-label={t("task.addGroup")}
        value={q}
        data-testid="group-input"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          if (hits[0] && (exact || !n)) onPick(hits[0][0]);
          else if (n) onCreate(q.trim());
          setQ("");
          setOpen(false);
        }}
      />
      {open && (hits.length > 0 || (n && !exact)) && (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 w-56 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
        >
          {hits.map(([gid, g]) => (
            <li key={gid}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  onPick(gid);
                  setQ("");
                  setOpen(false);
                }}
              >
                {g.title}
              </button>
            </li>
          ))}
          {n && !exact && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full rounded-sm px-2 py-1 text-left text-accent hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  onCreate(q.trim());
                  setQ("");
                  setOpen(false);
                }}
              >
                {t("task.createGroup", { title: q.trim() })}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Tell the server which task this panel shows, while it shows it: "selected task" buttons act on it (Settings →
 * Buttons). Repeated every minute so a device that went away stops counting after a few minutes.
 */
function useReportOpenTask(taskId: string, workspaceId: string) {
  useEffect(() => {
    const send = (id: string | null) =>
      void fetch("/api/internal/session/panel", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId: id, workspace: workspaceId }),
        keepalive: true,
      }).catch(() => undefined);
    send(taskId);
    const timer = window.setInterval(() => send(taskId), 60_000);
    return () => {
      window.clearInterval(timer);
      send(null);
    };
  }, [taskId, workspaceId]);
}
